import type { AppDispatch } from '@/store';
import { MIN_LEN } from '@/constants';
import { mapFileEntryToFileMetadata } from '@/store/mappers/mapSixDetail';
import { downloadAndDecryptFileThunk } from '@/store/thunks/poc/helia';
import type { FileEntry } from '@/types/SixDetail.types';
import { showErrorToast } from '@/utils/toast.utils';
import { userHasFileKey } from '@/utils/fileAccess.utils';

export interface DownloadedFilePayload {
  blob: Blob;
  filename: string;
}

interface GetDownloadedSixDetailFilePayloadParams {
  item: FileEntry;
  currentUserId: string | undefined;
  dispatch: AppDispatch;
  sixId: string;
}

export async function getDownloadedSixDetailFilePayload({
  item,
  currentUserId,
  dispatch,
  sixId,
}: GetDownloadedSixDetailFilePayloadParams): Promise<DownloadedFilePayload | undefined> {
  if (item.kind !== 'file') return undefined;
  if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return undefined;
  if (typeof item.cid !== 'string' || item.cid.trim().length === 0) {
    showErrorToast('File is missing CID metadata');
    return undefined;
  }
  if (!userHasFileKey(item, currentUserId)) {
    showErrorToast('You do not have access to decrypt this file');
    return undefined;
  }

  const fileMetadata = mapFileEntryToFileMetadata(item, sixId);
  const resultAction = await dispatch(
    downloadAndDecryptFileThunk({
      sixId,
      fileMetadata,
    }),
  );

  if (downloadAndDecryptFileThunk.fulfilled.match(resultAction)) {
    return {
      blob: resultAction.payload.decryptedFile,
      filename: resultAction.payload.originalFileName,
    };
  }

  showErrorToast(
    resultAction.payload?.message ??
      resultAction.error.message ??
      'Failed to download file',
  );

  return undefined;
}
